import React, { FunctionComponent } from "react";
import { useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  MenuItem,
  Backdrop,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
} from "@mui/material";
import { Save } from "@mui/icons-material";

import { CustomInput, CustomSelect, Button } from "..";
import { CustomTextArea } from "../CustomTextArea";
import { useEditWarrantyForm } from "./hook";
import { CustomSnackbar } from "../CustomSnackbar";
import { ViewChangesDialog } from "../ViewChangesDialog";
import { WarrantyHistory } from "../ViewChangesDialog/types";
import { FormSection } from "../FormSection";

export const EditWarrantyForm = (): JSX.Element => {
  const { warrantyId } = useParams();
  const {
    onCancel,
    warranty,
    dealers,
    adminNotes,
    newHomeOwnerName,
    setAdminNotes,
    setNewHomeOwnerName,
    onChange,
    onChangeSelect,
    onHandleCloseSnackbar,
    onFetchWarranty,
    onUpdate,
    snackbarState,
    isViewChangesDialogOpen,
    closeViewChangesDialog,
    openViewChangesDialog,
    isLoading,
  } = useEditWarrantyForm(parseInt(warrantyId!));

  const SectionTitle: FunctionComponent<{ title: string }> = (
    props,
  ): JSX.Element => {
    return (
      <Box sx={{ p: 2, backgroundColor: "#F5F5F5", borderRadius: 4, mt: 6 }}>
        <Typography fontSize={18} fontWeight="600" color="#1BA0E9">
          {props.title}
        </Typography>
      </Box>
    );
  };

  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress />
      </Backdrop>
      <Card sx={{ borderRadius: 4, width: "100%" }}>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            <Typography
              fontSize={22}
              fontWeight="600"
              color="#1BA0E9"
              textAlign="center"
            >
              Editing |{" "}
              <span style={{ color: "#00000099" }}>
                Warranty ID# {warrantyId} ({warranty.firstName},{" "}
                {warranty.lastName})
              </span>
            </Typography>
          </Box>
          <FormSection title="Personal Info">
            <Grid item>
              <CustomInput
                name="firstName"
                label={{ text: "First Name" }}
                value={warranty.firstName}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="lastName"
                label={{ text: "Last Name" }}
                value={warranty.lastName}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="streetNumber"
                label={{ text: "Street #" }}
                value={warranty.streetNumber}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="street"
                label={{ text: "Street" }}
                value={warranty.street}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="city"
                label={{ text: "City" }}
                value={warranty.city}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="state"
                label={{ text: "State" }}
                value={warranty.state}
                onChange={onChange}
                width={360}
                height={35}
                maxLength={2}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="zip"
                label={{ text: "Zip" }}
                value={warranty.zip}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="phone"
                label={{ text: "Phone" }}
                value={warranty.phone}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
          </FormSection>
          <FormSection title="Administrative Information">
            <Grid item>
              <CustomSelect
                name="dealer"
                label="Dealer"
                value={warranty.dealerId}
                onChange={onChangeSelect}
                width={360}
              >
                {dealers.map((item) => {
                  return <MenuItem value={item}>{item}</MenuItem>;
                })}
              </CustomSelect>
            </Grid>
            <Grid item>
              <CustomInput
                name="newHomeOwnerName"
                label={{ text: "New Home Owner" }}
                value={warranty.newHomeOwnerName}
                onChange={onChange}
                width={360}
                height={35}
              />
            </Grid>
            <Grid item>
              <CustomInput
                name="dateOfTransfer"
                label={{ text: "Date of Transfer" }}
                onChange={onChange}
                value={warranty.dateOfTransfer}
                width={360}
                height={35}
              />
            </Grid>
          </FormSection>
          <Box mt={4}>
            <CustomTextArea
              label={{ text: "Admin Notes" }}
              value={adminNotes}
              onChange={(event) => setAdminNotes(event.target.value)}
              name="adminNotes"
              width="100%"
              maxLength={500}
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              mt: 4,
              p: 2,
            }}
          >
            <Stack direction="row" justifyContent="space-between">
              <Button label="View Changes" onClick={openViewChangesDialog} />
              <Stack direction="row" spacing={2}>
                <Button variant="outlined" label="Cancel" onClick={onCancel} />
                <Button startIcon={<Save />} label="Save" onClick={onUpdate} />
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
      <ViewChangesDialog<WarrantyHistory>
        entity="WARRANTY"
        id={warrantyId!}
        label="Warranty"
        isOpen={isViewChangesDialogOpen}
        onClose={closeViewChangesDialog}
      />
      <CustomSnackbar
        variant="primary"
        level={snackbarState.level as any}
        open={snackbarState.isOpen}
        onClose={onHandleCloseSnackbar}
        message={snackbarState.message}
      />
    </Box>
  );
};
